import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Car,
  Plug,
  Power,
  Battery,
  Thermometer,
  Zap,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Usb,
  Network,
  HardDrive
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const Dashboard = ({ connectionStatus, vehicleInfo, onConnect, onDisconnect, isConnecting }) => {
  const [connectionType, setConnectionType] = useState("USB");
  const [port, setPort] = useState("/dev/ttyUSB0");
  const [liveData, setLiveData] = useState(null);
  const [recentDTCs, setRecentDTCs] = useState([]);
  const [showConnectDialog, setShowConnectDialog] = useState(false);

  useEffect(() => {
    if (connectionStatus.connected) {
      fetchLiveData();
      fetchRecentDTCs();
      const interval = setInterval(fetchLiveData, 2000);
      return () => clearInterval(interval);
    }
  }, [connectionStatus.connected]);

  const fetchLiveData = async () => {
    try {
      const response = await axios.get(`${API}/live-data/snapshot`);
      setLiveData(response.data);
    } catch (error) {
      console.error("Failed to fetch live data:", error);
    }
  };

  const fetchRecentDTCs = async () => {
    try {
      const response = await axios.get(`${API}/dtc/history`);
      setRecentDTCs(response.data.slice(0, 3));
    } catch (error) {
      console.error("Failed to fetch DTCs:", error);
    }
  };

  const handleConnect = () => {
    onConnect(connectionType, port);
    setShowConnectDialog(false);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical": return "text-[#FF3B30]";
      case "warning": return "text-[#FF9F0A]";
      default: return "text-[#007AFF]";
    }
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="header-bar">
        <div>
          <h1 className="header-title">Dashboard</h1>
          <p className="header-subtitle">Tesla Model S 2013 Diagnostic Interface</p>
        </div>
        
        {connectionStatus.connected ? (
          <Button
            onClick={onDisconnect}
            variant="outline"
            className="bg-transparent border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 uppercase tracking-widest font-bold"
            data-testid="disconnect-btn"
          >
            <Power className="mr-2 h-4 w-4" />
            Disconnect
          </Button>
        ) : (
          <Button
            onClick={() => setShowConnectDialog(true)}
            className="btn-primary"
            data-testid="connect-btn"
          >
            <Plug className="mr-2 h-4 w-4" />
            Connect Vehicle
          </Button>
        )}
      </div>

      {/* Main Grid */}
      <div className="bento-grid">
        {/* Vehicle Overview Card */}
        <div className="bento-card span-8">
          <div className="card-header">
            <h2 className="card-title">Vehicle Overview</h2>
            {connectionStatus.connected && (
              <span className="badge-success">Online</span>
            )}
          </div>
          
          <div className="flex gap-8 items-center">
            <div className="flex-1">
              <img 
                src="https://images.unsplash.com/photo-1602388081583-c5dc8bf04c3a?w=600&h=400&fit=crop"
                alt="Tesla Model S"
                className="rounded-lg w-full h-48 object-cover opacity-80"
              />
            </div>
            
            <div className="flex-1 space-y-4">
              {vehicleInfo ? (
                <>
                  <div>
                    <p className="data-label">Vehicle Identification Number</p>
                    <p className="vin-display mt-2">{vehicleInfo.vin}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="data-label">Model</p>
                      <p className="font-heading text-xl text-white mt-1">Model S P85</p>
                    </div>
                    <div>
                      <p className="data-label">Year</p>
                      <p className="font-heading text-xl text-white mt-1">2013</p>
                    </div>
                    <div>
                      <p className="data-label">Firmware</p>
                      <p className="font-mono text-lg text-[#007AFF] mt-1">{vehicleInfo.firmware_version}</p>
                    </div>
                    <div>
                      <p className="data-label">Battery</p>
                      <p className="font-heading text-xl text-white mt-1">85 kWh</p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Car className="h-16 w-16 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">No vehicle connected</p>
                  <p className="text-white/30 text-sm mt-2">Connect via USB or Ethernet to begin diagnostics</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bento-card span-4">
          <div className="card-header">
            <h2 className="card-title">Quick Stats</h2>
            {liveData && (
              <span className="data-pulse text-xs text-[#30D158] font-mono">LIVE</span>
            )}
          </div>
          
          {liveData ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Battery className="h-5 w-5 text-[#30D158]" />
                  <span className="text-white/60">Battery SOC</span>
                </div>
                <span className="font-mono text-xl text-white">{liveData.battery_soc.toFixed(1)}%</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Zap className="h-5 w-5 text-[#007AFF]" />
                  <span className="text-white/60">Pack Voltage</span>
                </div>
                <span className="font-mono text-xl text-white">{liveData.battery_voltage.toFixed(1)}V</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Thermometer className="h-5 w-5 text-[#FF9F0A]" />
                  <span className="text-white/60">Battery Temp</span>
                </div>
                <span className="font-mono text-xl text-white">{liveData.battery_temp.toFixed(1)}°C</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <HardDrive className="h-5 w-5 text-[#007AFF]" />
                  <span className="text-white/60">12V System</span>
                </div>
                <span className="font-mono text-xl text-white">{liveData.aux_12v_voltage.toFixed(1)}V</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-white/30">
              <p>Connect vehicle to view live data</p>
            </div>
          )}
        </div>

        {/* Connection Card */}
        <div className="bento-card span-4">
          <div className="card-header">
            <h2 className="card-title">Connection</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 glass-card">
              <Usb className="h-8 w-8 text-[#007AFF]" />
              <div>
                <p className="font-heading text-sm uppercase tracking-wider text-white">USB Connection</p>
                <p className="text-xs text-white/50 font-mono">/dev/ttyUSB0</p>
              </div>
              <div className={`ml-auto h-3 w-3 rounded-full ${connectionStatus.connected && connectionType === 'USB' ? 'bg-[#30D158]' : 'bg-white/20'}`}></div>
            </div>
            
            <div className="flex items-center gap-4 p-4 glass-card">
              <Network className="h-8 w-8 text-[#007AFF]" />
              <div>
                <p className="font-heading text-sm uppercase tracking-wider text-white">Ethernet</p>
                <p className="text-xs text-white/50 font-mono">eth0:8080</p>
              </div>
              <div className={`ml-auto h-3 w-3 rounded-full ${connectionStatus.connected && connectionType === 'Ethernet' ? 'bg-[#30D158]' : 'bg-white/20'}`}></div>
            </div>
          </div>
        </div>

        {/* Recent DTCs */}
        <div className="bento-card span-4">
          <div className="card-header">
            <h2 className="card-title">Recent DTCs</h2>
            <a href="/diagnostics" className="text-[#007AFF] text-sm hover:underline">View All</a>
          </div>
          
          {recentDTCs.length > 0 ? (
            <div className="space-y-3">
              {recentDTCs.map((dtc, index) => (
                <div key={index} className="flex items-center gap-3 p-3 glass-card">
                  <AlertTriangle className={`h-5 w-5 ${getSeverityColor(dtc.severity)}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm text-[#007AFF]">{dtc.code}</p>
                    <p className="text-xs text-white/50 truncate">{dtc.description}</p>
                  </div>
                  <span className={`badge-${dtc.severity === 'critical' ? 'error' : dtc.severity}`}>
                    {dtc.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-white/30">
              <CheckCircle className="h-10 w-10 mx-auto mb-2 text-[#30D158]/50" />
              <p className="text-sm">No recent trouble codes</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bento-card span-4">
          <div className="card-header">
            <h2 className="card-title">Quick Actions</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <a 
              href="/diagnostics" 
              className="p-4 glass-card hover:bg-white/5 transition-colors cursor-pointer"
              data-testid="quick-scan-dtc"
            >
              <AlertTriangle className="h-6 w-6 text-[#FF9F0A] mb-2" />
              <p className="font-heading text-sm uppercase tracking-wider">Scan DTCs</p>
            </a>
            
            <a 
              href="/live-data" 
              className="p-4 glass-card hover:bg-white/5 transition-colors cursor-pointer"
              data-testid="quick-live-data"
            >
              <Zap className="h-6 w-6 text-[#007AFF] mb-2" />
              <p className="font-heading text-sm uppercase tracking-wider">Live Data</p>
            </a>
            
            <a 
              href="/modules" 
              className="p-4 glass-card hover:bg-white/5 transition-colors cursor-pointer"
              data-testid="quick-modules"
            >
              <RefreshCw className="h-6 w-6 text-[#30D158] mb-2" />
              <p className="font-heading text-sm uppercase tracking-wider">Reset Module</p>
            </a>
            
            <a 
              href="/safety" 
              className="p-4 glass-card hover:bg-white/5 transition-colors cursor-pointer"
              data-testid="quick-safety"
            >
              <AlertTriangle className="h-6 w-6 text-[#FF3B30] mb-2" />
              <p className="font-heading text-sm uppercase tracking-wider">Safety Info</p>
            </a>
          </div>
        </div>
      </div>

      {/* Connect Dialog */}
      <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider">Connect Vehicle</DialogTitle>
            <DialogDescription className="text-white/60">
              Select connection type and port to establish communication with your Tesla Model S.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div>
              <label className="text-sm text-white/60 mb-2 block">Connection Type</label>
              <Select value={connectionType} onValueChange={setConnectionType}>
                <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="connection-type-select">
                  <SelectValue placeholder="Select connection type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10">
                  <SelectItem value="USB" className="text-white hover:bg-white/10">
                    <div className="flex items-center gap-2">
                      <Usb className="h-4 w-4" />
                      USB Connection
                    </div>
                  </SelectItem>
                  <SelectItem value="Ethernet" className="text-white hover:bg-white/10">
                    <div className="flex items-center gap-2">
                      <Network className="h-4 w-4" />
                      Ethernet Connection
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-white/60 mb-2 block">Port / Address</label>
              <input
                type="text"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-mono focus:border-[#007AFF] focus:outline-none"
                placeholder={connectionType === "USB" ? "/dev/ttyUSB0" : "192.168.1.100:8080"}
                data-testid="connection-port-input"
              />
            </div>
            
            <div className="p-4 bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 rounded">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-[#FF9F0A] mt-0.5" />
                <div className="text-sm">
                  <p className="text-[#FF9F0A] font-medium">Safety Notice</p>
                  <p className="text-white/60 mt-1">
                    Ensure vehicle is in Park with parking brake engaged before connecting diagnostic equipment.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConnectDialog(false)}
              className="bg-transparent border-white/20 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConnect}
              disabled={isConnecting}
              className="btn-primary"
              data-testid="confirm-connect-btn"
            >
              {isConnecting ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Plug className="mr-2 h-4 w-4" />
                  Connect
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
